// The composer's placeholder for the AI mode (AI_ASSISTANT_SPEC §8: "placeholder
// per mode, never wrapping: short copy + wider bar") — a polyglot KEY the
// composer translates. el.call('assistantModePlaceholder', root) or a plain call
// with the root state; the mode is the per-user `assistantMode` ('assistant' |
// 'dashboard' | 'tools' | 'apps' | 'presentation' | 'fly', §12.1 — the assistant lane's
// key), 'assistant' when unset. Apps is the FRONTEND app now (a build), so it
// has its own line; Tools works inside the installed platform apps.
// In a call with Fly on, on the call (meet functions/meetFly.js): a Fly, a
// Dashboard and a Presentation ask go to everyone's theater — the line says
// so; meetFlyComposerShared decides. The call's keys are read on EVERY run,
// before any gate (`void` — a tracking read only): the meet function is
// registered only once the call's meet context is folded, and a run that read
// none of them would never re-run when the person joins a call with Fly on.
const _composerAsksTheCall = function (r, m) {
  void [r.livekitConnected, r.meetFly, r.currentPath, r.meetView]
  if (m !== 'fly' && m !== 'dashboard' && m !== 'presentation') return false
  const fns = this && this.context && this.context.functions
  return (
    !!fns &&
    typeof fns.meetFlyComposerShared === 'function' &&
    !!this.call('meetFlyComposerShared', r)
  )
}

// THE VIEW, not the stored mode (the workspace AI overhaul, 2026-10-28): the
// line names where the next ask lands — the ONE reader the thread head and the
// Ask menu read too (workspace functions/assistantAskRoute.js
// assistantEffectiveView: a thread on screen is Chat, a stored Dashboard reads
// as Pages). A host without that reader (canvas standalone) reads the mode.
// Both keys are read first, whichever wins (reactive callers track both).
export const assistantModePlaceholder = function (root) {
  const r = root || (this && this.state && this.state.root) || {}
  const mode = r.assistantMode
  const fns = this && this.context && this.context.functions
  const view =
    fns && typeof fns.assistantEffectiveView === 'function'
      ? this.call('assistantEffectiveView', r)
      : null
  const byView = {
    chat: 'assistant',
    home: 'assistant',
    pages: 'fly',
    build: 'apps',
    present: 'presentation'
  }
  const m = byView[view] || mode
  const call = _composerAsksTheCall.call(this, r, m)
  if (m === 'dashboard')
    return call ? 'ai.composer.placeholderDashboardCall' : 'ai.composer.placeholderDashboard'
  if (m === 'tools') return 'ai.composer.placeholderTools'
  if (m === 'apps') return 'ai.composer.placeholderAppsBuild'
  if (m === 'presentation')
    return call ? 'ai.composer.placeholderPresentationCall' : 'ai.composer.placeholderPresentation'
  if (m === 'fly') return call ? 'ai.composer.placeholderFlyCall' : 'ai.composer.placeholderFly'
  return 'ai.composer.placeholderAssistant'
}

// The SHORT line per mode, for a composer too narrow for the long one (the
// small pill at 390, a Georgian line in the desktop pill — design-d2's live
// review of deploy 8: "no cut text").
export const assistantModePlaceholderShort = function (root) {
  const r = root || (this && this.state && this.state.root) || {}
  const mode = r.assistantMode
  const fns = this && this.context && this.context.functions
  const view =
    fns && typeof fns.assistantEffectiveView === 'function'
      ? this.call('assistantEffectiveView', r)
      : null
  const byView = {
    chat: 'assistant',
    home: 'assistant',
    pages: 'fly',
    build: 'apps',
    present: 'presentation'
  }
  const m = byView[view] || mode
  const call = _composerAsksTheCall.call(this, r, m)
  if (m === 'dashboard')
    return call
      ? 'ai.composer.placeholderDashboardCallShort'
      : 'ai.composer.placeholderDashboardShort'
  if (m === 'tools') return 'ai.composer.placeholderToolsShort'
  if (m === 'apps') return 'ai.composer.placeholderAppsBuildShort'
  if (m === 'presentation')
    return call
      ? 'ai.composer.placeholderPresentationCallShort'
      : 'ai.composer.placeholderPresentationShort'
  if (m === 'fly')
    return call ? 'ai.composer.placeholderFlyCallShort' : 'ai.composer.placeholderFlyShort'
  return 'ai.composer.placeholderAssistantShort'
}

// The line the composer SHOWS, chosen by a MEASURED fit, never a breakpoint:
// the long line when its rendered width fits the field's text room, else the
// short one. `this` = the textarea element. The room is the field's content
// width (its box minus the inline padding); the width is the line measured in
// the field's own font. Before the field has a box (the first render) the
// long line stands; assistantModePlaceholderRefit re-chooses once it has one.
// The short line is translated only when the long one does not fit, and a
// field whose line, room and font are the ones it last measured keeps its
// last choice (`el.__phFit`) without a new measure.
// `probe` (tests): { room: px, width: (text) => px }.
// The TINY line per mode: the shortest natural word or two, for a field too
// narrow even for the short line (the 390 pill: 136 px of text room beside
// five controls — measured live on deploy 9).
export const assistantModePlaceholderTiny = function (root) {
  const r = root || (this && this.state && this.state.root) || {}
  const mode = r.assistantMode
  const fns = this && this.context && this.context.functions
  const view =
    fns && typeof fns.assistantEffectiveView === 'function'
      ? this.call('assistantEffectiveView', r)
      : null
  const byView = {
    chat: 'assistant',
    home: 'assistant',
    pages: 'fly',
    build: 'apps',
    present: 'presentation'
  }
  const m = byView[view] || mode
  if (m === 'dashboard') return 'ai.composer.placeholderDashboardTiny'
  if (m === 'tools') return 'ai.composer.placeholderToolsTiny'
  if (m === 'apps') return 'ai.composer.placeholderAppsBuildTiny'
  if (m === 'presentation') return 'ai.composer.placeholderPresentationTiny'
  if (m === 'fly') return 'ai.composer.placeholderFlyTiny'
  return 'ai.composer.placeholderAssistantTiny'
}

export const assistantModePlaceholderFit = function (root, probe) {
  const el = this
  const r = root || (el && el.state && el.state.root) || {}
  const long = el.call('polyglot', el.call('assistantModePlaceholder', r))
  let room = probe && typeof probe.room === 'number' ? probe.room : null
  let width = probe && typeof probe.width === 'function' ? probe.width : null
  const node = el && el.node
  let seen = null
  if (
    (room === null || !width) &&
    node &&
    node.isConnected &&
    typeof getComputedStyle === 'function'
  ) {
    const cs = getComputedStyle(node)
    if (room === null) {
      room =
        node.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0)
    }
    const font = cs.font || `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
    if (!width && typeof document !== 'undefined') {
      // the font face's load state is part of the key: a measure taken with
      // the fallback face (the brand face still loading) is not reused
      const fonts = typeof document !== 'undefined' ? document.fonts : null
      seen = long + '|' + room + '|' + font + '|' + (fonts ? fonts.status : '')
      // the face is not loaded yet: measure now, and choose again once it is
      if (fonts && fonts.status !== 'loaded' && fonts.ready && !el.__phFontWait) {
        el.__phFontWait = true
        fonts.ready.then(() => {
          el.__phFontWait = false
          if (el.node && el.node.isConnected) el.call('assistantModePlaceholderRefit', r)
        })
      }
      if (el.__phFit && el.__phFit.seen === seen) return el.__phFit.text
      const cache = globalThis.__placeholderFitCanvas || (globalThis.__placeholderFitCanvas = {})
      if (!cache.ctx) cache.ctx = document.createElement('canvas').getContext('2d')
      const ctx = cache.ctx
      if (ctx) {
        ctx.font = font
        width = (t) => ctx.measureText(t).width
      }
    }
  }
  if (!(room > 0) || !width) return long
  // long -> short -> tiny, each by its MEASURED width; nothing at all rather
  // than a cut line (Nika's law: no cut text). Each tier is CHECKED — the
  // short line used to be the fallback unmeasured, and on deploy 9 every
  // Georgian short line was cut in the 390 pill.
  let text = ''
  for (const key of [
    null,
    el.call('assistantModePlaceholderShort', r),
    el.call('assistantModePlaceholderTiny', r)
  ]) {
    const line = key === null ? long : el.call('polyglot', key)
    if (typeof line === 'string' && line && width(line) <= room) {
      text = line
      break
    }
  }
  if (seen) el.__phFit = { seen, text }
  return text
}

// Re-choose after the field has a box, and on a resize: the placeholder prop
// ran before layout (its first evaluation has no room to measure).
export const assistantModePlaceholderRefit = function (root) {
  const el = this
  const node = el && el.node
  if (!node || !node.isConnected) return
  const text = el.call('assistantModePlaceholderFit', root)
  if (typeof text === 'string' && node.getAttribute('placeholder') !== text) {
    node.setAttribute('placeholder', text)
  }
}

// Choose now AND whenever the field's box changes: its room is final only after
// the composer row has laid out — the context chip, the buttons, data that
// lands seconds later. Measured on dev at 390 px: 207 px at render, 195 px at
// 10 s, 155 px at 13 s; the long Fly line stood cut ("Ask anything — get a
// pag") until something re-rendered. ONE observer per field; the fit's cache
// makes a refit free when nothing moved; a detached field lets go of it.
export const assistantModePlaceholderSettle = function (root) {
  const el = this
  el.call('assistantModePlaceholderRefit', root)
  const node = el && el.node
  if (!node || typeof ResizeObserver !== 'function' || el.__phObserved === node) return
  if (el.__phObserver) el.__phObserver.disconnect()
  el.__phObserved = node
  const observer = new ResizeObserver(() => {
    if (!node.isConnected) {
      observer.disconnect()
      if (el.__phObserver === observer) {
        el.__phObserver = null
        el.__phObserved = null
      }
      return
    }
    el.call('assistantModePlaceholderRefit', root)
  })
  el.__phObserver = observer
  observer.observe(node)
}
